// MA-344 (PR-B) — resolveStoneTap: the PURE tap-routing decision for the
// Journey tab's stones (D4, docs/build/specs/MA-343v2-journey-delight-
// design.md). buildJourneyView (journeyView.ts) computes each stone's
// `tapEligibility` from the fold + the D4 window/chapter-cap/zero-pool
// rules; THIS module turns that eligibility (plus the one piece of state
// journeyView deliberately keeps out of its own purity — the child's
// placement floor, which needs Progress) into either a navigation target
// or a narrate decision. No engine write, no queue, no assignment — a tap
// is ONLY a navigate or a narrate (D4's own hard rule).
//
// ⭐MA-504: a narrate resolution used to also carry a `.text` field, looked
// up from JOURNEY_TAP_NARRATE_TEXT (narrationUiStrings.ts). MA-500 found
// by exhaustive grep that nothing in the live tree ever read that `.text`
// — MA-496's JourneyPath.tsx rewrite speaks the coarser, always-fires
// `speakTextForStone(stone.kidLabel, stone.status)` for every non-eligible
// tap instead, and never calls resolveStoneTap(...).text. Per the
// operator's MA-504 product call, that dead `.text` field and its backing
// const were deleted rather than revived — `reason` is the real, tested
// eligibility semantics this module still owns; `text` was never live.
//
// Routing mirrors PathHome.tsx's `navigateToSubject` (lines ~445-466)
// EXACTLY, on the TAPPED STONE'S OWN id (never a recommendation lookup —
// a stone tap always targets exactly the stone the kid touched):
//   • math strand                    → /play/session?skill=<id>
//   • FOUNDATION_SKILL_IDS (reading) → /play/foundation?skill=<id>
//   • any other reading strand id    → /play/reading?skill=<id>
// NEVER `practice=1`/`practice=true` on a kid-initiated journey tap — the
// `practice` search param exists ONLY for the parent-authority SkillTree
// "practice this skill" button (practiceTargetForSkill) and is never set
// here (see the D4 "no practice=1" hard fence).
//
// Foundation-tier + isBelowFloor is decided HERE, not in journeyView,
// because it needs Progress (readingModuleId/placementStatus), which is
// per-child mutable state the pure builder is never handed. Checking it
// BEFORE navigating (rather than letting /play/foundation's own beforeLoad
// guard redirect silently) is deliberate: a silent below-floor redirect
// would strand the kid on a DIFFERENT skill than the stone they tapped
// with no explanation; narrating instead keeps them on Journey with a
// kid-honest reason (D4).
import { subjectForSkill, FOUNDATION_SKILL_IDS } from "./skillMeta";
import { isBelowFloor, guardAllowsBelowFloor, type PlacementFloor } from "./placementFloor";
import { belowFloor, type FloorProgressLike } from "./evidenceFloor";
import type { TapEligibility } from "./journeyView";

export type StoneTapNarrateReason =
  | "beyond-ahead"
  | "beyond-behind"
  | "zero-pool"
  | "foundation-below-floor";

export type StoneTapResolution =
  | { kind: "navigate"; to: "/play/session"; search: { skill: string } }
  | { kind: "navigate"; to: "/play/reading"; search: { skill: string } }
  | { kind: "navigate"; to: "/play/foundation"; search: { skill: string } }
  | { kind: "narrate"; reason: StoneTapNarrateReason };

/** Minimal shape resolveStoneTap needs from a JourneyStone — just `id` +
 *  `tapEligibility` (never `status`/`kidLabel`/etc, so a caller can pass a
 *  bare `{id, tapEligibility}` literal in a unit test without constructing
 *  a full JourneyStone). */
export interface StoneTapInput {
  id: string;
  tapEligibility: TapEligibility;
}

/** Minimal Progress shape placementFloor() needs — re-exported here so
 *  call sites don't need to import the full Progress type from
 *  App/progress.ts just to call this function. */
// MA-837: the shared evidence-floor rule's input (placement fields + the
// provenance/age/diagnostic fields it reads; all optional — a progress
// without them reads as "unknown age -> evidence", the pre-MA-775 behaviour).
export type StoneTapProgressLike = FloorProgressLike;

function narrate(reason: StoneTapNarrateReason): StoneTapResolution {
  return { kind: "narrate", reason };
}

/** Pure. No React, no DOM, no navigation side effect — returns the
 *  DECISION only; the caller (JourneyPath.tsx) performs the actual
 *  `navigate()` or `speak()`. Total over TapEligibility's full union so a
 *  future eligibility variant can't silently fall through unhandled. */
export function resolveStoneTap(
  stone: StoneTapInput,
  progress: StoneTapProgressLike | null | undefined,
  registryAge?: number | null,
  // MA-851: the floor skill's demotion valve is lowered (the guard then lets its prerequisites through)
  valveLowered = false,
): StoneTapResolution {
  switch (stone.tapEligibility) {
    case "narrate:zero-pool":
      return narrate("zero-pool");
    case "narrate:beyond-ahead":
      return narrate("beyond-ahead");
    case "narrate:beyond-behind":
      return narrate("beyond-behind");
    case "none":
      // Defensive only — journeyView never emits `none` for a stone this
      // component would ever attach a tap handler to (data-tappable is
      // false AND non-launch narrate stones still handle their own tap);
      // treat it the same as an out-of-window-ahead stone rather than
      // silently doing nothing (a tap must always resolve to SOMETHING).
      return narrate("beyond-ahead");
    case "launch": {
      const isMath = subjectForSkill(stone.id) === "math";
      if (isMath) {
        return { kind: "navigate", to: "/play/session", search: { skill: stone.id } };
      }
      if (FOUNDATION_SKILL_IDS.includes(stone.id)) {
        const floor: PlacementFloor | null = belowFloor("reading", progress ?? null, registryAge);
        if (isBelowFloor(stone.id, floor) && !guardAllowsBelowFloor(stone.id, floor, valveLowered)) {
          return narrate("foundation-below-floor");
        }
        return { kind: "navigate", to: "/play/foundation", search: { skill: stone.id } };
      }
      return { kind: "navigate", to: "/play/reading", search: { skill: stone.id } };
    }
  }
}
